import React from 'react';
import { EditableCheckboxInput } from '../components/EditableCell/EditableCheckboxInput';
import { EditableContainerStatusAutocomplete } from '../components/EditableCell/EditableContainerStatusAutocomplete';
import { EditableContainerTypeAutocomplete } from '../components/EditableCell/EditableContainerTypeAutocomplete';
import { EditableCustomsClearanceInput } from '../components/EditableCell/EditableCustomsClearanceInput';
import { EditableDateInput } from '../components/EditableCell/EditableDateInput';
import { EditableDriverFieldInput } from '../components/EditableCell/EditableDriverFieldInput';
import { EditableLoadingDeliveryLocationAutocomplete } from '../components/EditableCell/EditableLoadingDeliveryLocationAutocomplete';
import { EditableLotAutocomplete } from '../components/EditableCell/EditableLotAutocomplete';
import { EditableProductGroupAutocomplete } from '../components/EditableCell/EditableProductGroupAutocomplete';
import { EditableReferenceNumbersInput } from '../components/EditableCell/EditableReferenceNumbersInput';
import { EditableShippingLineAutocomplete } from '../components/EditableCell/EditableShippingLineAutocomplete';
import { EditableTerminalAutocomplete } from '../components/EditableCell/EditableTerminalAutocomplete';
import { EditableTextInput } from '../components/EditableCell/EditableTextInput';
import { EditableTradeTypeAutocomplete } from '../components/EditableCell/EditableTradeTypeAutocomplete';
import { EditableTriangulationInput } from '../components/EditableCell/EditableTriangulationInput';
import { EditableTruckerAutocomplete } from '../components/EditableCell/EditableTruckerAutocomplete';
import { EditableTruckerRateInput } from '../components/EditableCell/EditableTruckerRateInput';
import { EditableWeighingInput } from '../components/EditableCell/EditableWeighingInput';

interface EditComponentProps {
  value: any;
  onChange: (value: any) => void;
  onSave: (value: any, skipDriverCheck?: boolean) => void;
  onCancel: () => void;
  onRowFieldSave?: (
    columnKey: string,
    value: any,
    skipDriverCheck?: boolean,
  ) => void;
  customerId?: number | null;
  rowData?: any;
  hasValidationError?: boolean;
}

type EditComponentFactory = (props: EditComponentProps) => React.ReactElement;

const editComponentMap: Record<string, EditComponentFactory> = {
  shippingLine: (props) => (
    <EditableShippingLineAutocomplete {...props} disableClearable={false} />
  ),
  containerType: (props) => (
    <EditableContainerTypeAutocomplete {...props} disableClearable={false} />
  ),
  loadingDeliveryLocation: (props) => (
    <EditableLoadingDeliveryLocationAutocomplete
      {...props}
      customerId={props.customerId || null}
      disableClearable={false}
    />
  ),
  pickupLocation: (props) => {
    if (props.rowData?.tradeType === 'IMPORT') {
      return (
        <EditableTerminalAutocomplete {...props} disableClearable={false} />
      );
    }
    return <EditableLotAutocomplete {...props} disableClearable={false} />;
  },
  deliveryLocation: (props) => {
    if (props.rowData?.tradeType === 'IMPORT') {
      return <EditableLotAutocomplete {...props} disableClearable={false} />;
    }
    return <EditableTerminalAutocomplete {...props} disableClearable={false} />;
  },
  productGroup: (props) => (
    <EditableProductGroupAutocomplete {...props} disableClearable={false} />
  ),
  truckerRate: (props) => <EditableTruckerRateInput {...props} />,
  referenceNumbers: (props) => <EditableReferenceNumbersInput {...props} />,
  pickupDate: (props) => <EditableDateInput {...props} />,
  loadingDeliveryDate: (props) => <EditableDateInput {...props} />,
  deliveryDate: (props) => <EditableDateInput {...props} />,
  trucker: (props) => (
    <EditableTruckerAutocomplete {...props} disableClearable={false} />
  ),
  customsClearance: (props) => <EditableCustomsClearanceInput {...props} />,
  triangulation: (props) => <EditableTriangulationInput {...props} />,
  weighing: (props) => <EditableWeighingInput {...props} />,
  containerStatus: (props) => (
    <EditableContainerStatusAutocomplete {...props} disableClearable={false} />
  ),
  driverName: (props) => (
    <EditableDriverFieldInput {...props} field='driverName' />
  ),
  driverDocumentNumber: (props) => (
    <EditableDriverFieldInput {...props} field='driverDocumentNumber' />
  ),
  driverPhone: (props) => (
    <EditableDriverFieldInput {...props} field='driverPhone' />
  ),
  shippingLineHaulage: (props) => <EditableCheckboxInput {...props} />,
  tradeType: (props) => <EditableTradeTypeAutocomplete {...props} />,
};

export const getEditComponent = (
  columnKey: string,
  props: EditComponentProps,
): React.ReactElement => {
  const factory = editComponentMap[columnKey];
  return factory ? factory(props) : <EditableTextInput {...props} />;
};
